import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';

import Header from '../components/global/header';
import Footer from '../components/global/footer';
import AppsList from '../components/global/footer/apps';
import defaultMedicSvg from '../../assets/img/next/svg/medic.svg';
import { Button, Modal } from 'react-bootstrap';
import { getAppointmentByCode, approveAppointmentByCode, cancelAppointmentByCode } from '../../api';
import { updateCity } from '../../actions';

const CODE_RE = /^[a-zA-Z0-9]{8}$/;

function AppointmentByCode({ cities, updateCity, appointment_promo }) {
  const { code } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [city, setCity] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | 404 | 410 | error
  const [actionStatus, setActionStatus] = useState(null); // null | 'approving' | 'cancelling' | 'approved' | 'cancelled' | 'action_error'
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const pharmPromo = appointment_promo ?? '';

  useEffect(() => {
    if (!CODE_RE.test(code)) {
      setStatus('404');
      return;
    }

    getAppointmentByCode(code)
      .then((response) => {
        const data = response.data || response;
        const cityId = data?.division?.city_id;
        const found = cities?.find((c) => c.id === cityId) ?? null;
        if (cityId && found) {
          updateCity({ ...found, position: { lat: +found.lat, lng: +found.lon } });
        }
        setAppointment(data);
        setCity(found);
        setStatus('ok');
      })
      .catch((err) => {
        if (err?.status === 404) setStatus('404');
        else if (err?.status === 410) setStatus('410');
        else setStatus('error');
      });
  }, [code, cities]);

  const handleApprove = () => {
    setActionStatus('approving');
    approveAppointmentByCode(code)
      .then((response) => {
        const data = response.data || response;
        setAppointment((prev) => ({ ...prev, approved_at: data.approved_at || new Date().toISOString() }));
        setActionStatus('approved');
      })
      .catch(() => setActionStatus('action_error'));
  };

  const handleCancel = () => {
    setShowCancelConfirm(false);
    setActionStatus('cancelling');
    cancelAppointmentByCode(code)
      .then(() => setActionStatus('cancelled'))
      .catch(() => setActionStatus('action_error'));
  };

  const renderAppointmentInfo = () => {
    if (!appointment) return null;

    const { doctor, division, institution, work_date, time_from, cabinet_num, patient, speciality, slot_type, is_video_conference, approved_at } = appointment;

    const doctorName = doctor?.is_virtual
      ? appointment.job?.cabinet_name || (appointment.job?.cabinet_num ? `Кабінет №${appointment.job.cabinet_num}` : 'Кабінет')
      : slot_type === 'screening_40_plus'
        ? 'Кабінет скринінгу'
        : doctor?.full_name;

    const dateStr = work_date ? moment(work_date).format('DD.MM.YYYY') : '—';
    const timeStr = time_from ? time_from.slice(0, 5) : '—';

    // Approve button is active if approved_at is empty OR approved_at was set more than 24h ago
    const approveEnabled = !approved_at || moment().diff(moment(approved_at), 'hours') > 24;
    const isCancelled = appointment.deleted === true || actionStatus === 'cancelled';

    return (
      <div className="personal-visits-wrapper extended">
        <div className="personal-visits">
          <ul className="personal-visits-list">
            <li className="personal-visits-list__item">
              {patient?.short_name && (
                <div className="visit-by-code-page__greeting">
                  <p>Шановний(-а) {patient.short_name}!</p>
                  <p>Інформація про Ваш запис на прийом:</p>
                </div>
              )}

              <div className="streaming-records-item" style={{ width: '100%' }}>
                <div className="streaming-records-item__information">
                  <div className="streaming-records-item__user">
                    <div
                      className="streaming-records-item__user-icon"
                      style={{ backgroundImage: `url(${doctor?.photo ?? defaultMedicSvg})` }}
                    />
                    <div className="streaming-records-item__user-about">
                      <div className="streaming-records-item__user-title">{doctorName}</div>
                      {slot_type !== 'screening_40_plus' && speciality?.name && (
                        <div className="streaming-records-item__user-description">{speciality.name}</div>
                      )}
                      {slot_type === 'paid' && (
                        <div className="streaming-records-item__address-list-item-title">
                          <span style={{ color: '#fff', backgroundColor: '#b8a817', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>
                            Платні послуги
                          </span>
                        </div>
                      )}
                      {slot_type === 'screening_40_plus' && (
                        <div className="streaming-records-item__address-list-item-title">
                          <span style={{ color: '#fff', backgroundColor: '#17a2b8', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>
                            Скринінг здоров'я 40+
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="streaming-records-item__address">
                    <ul className="streaming-records-item__address-list">
                      <li className="streaming-records-item__address-list-item">
                        <div className="streaming-records-item__address-list-item-title">Дата та час</div>
                        <div className="streaming-records-item__address-list-item-description">
                          <strong>{dateStr} о {timeStr}</strong>
                        </div>
                      </li>
                      <li className="streaming-records-item__address-list-item">
                        <div className="streaming-records-item__address-list-item-title">Адреса</div>
                        <div className="streaming-records-item__address-list-item-description">
                          {division?.address?.display_text || '—'}
                        </div>
                      </li>
                      {cabinet_num && (
                        <li className="streaming-records-item__address-list-item">
                          <div className="streaming-records-item__address-list-item-title">Кабінет</div>
                          <div className="streaming-records-item__address-list-item-description">
                            <strong>{cabinet_num}</strong>
                          </div>
                        </li>
                      )}
                      {division?.full_name && (
                        <li className="streaming-records-item__address-list-item">
                          <div className="streaming-records-item__address-list-item-title">Заклад</div>
                          <div className="streaming-records-item__address-list-item-description">
                            {division.full_name}
                          </div>
                        </li>
                      )}
                      {!!division?.contacts?.length && division.contacts.some(({ type }) => type === 'phone') && (
                        <li className="streaming-records-item__address-list-item">
                          <div className="streaming-records-item__address-list-item-title">Телефон реєстратури</div>
                          <div className="streaming-records-item__address-list-item-description">
                            {division.contacts
                              .filter(({ type }) => type === 'phone')
                              .map(({ value }) => value)
                              .join(', ')}
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>

                  {is_video_conference === 1 && (
                    <div className="text-center" style={{ marginTop: '12px' }}>
                      <p>Це запис на онлайн відеоконсультацію.</p>
                    </div>
                  )}

               {pharmPromo && (
                    <div className="visit-by-code-page__pharm-text">
                    <div className="visit-by-code-page__pharm-text-icon">
                        <i className="fas fa-pills" />
                    </div>
                    <p className="visit-by-code-page__pharm-text-body" dangerouslySetInnerHTML={{ __html: pharmPromo }} />
                    </div>
                )}

                  {!isCancelled && (
                    <div className="appointment-by-code__actions">
                      <Button
                        variant={!approveEnabled || actionStatus === 'approved' ? 'outline-success' : 'success'}
                        disabled={!approveEnabled || actionStatus === 'approving' || actionStatus === 'approved'}
                        onClick={handleApprove}
                        className="appointment-by-code__btn"
                      >
                        {actionStatus === 'approving'
                          ? <><i className="fas fa-spinner fa-spin" />Підтверджуємо...</>
                          : (!approveEnabled || actionStatus === 'approved')
                            ? <><i className="fas fa-check-circle" />Підтверджено</>
                            : <><i className="fas fa-calendar-check" />Підтвердити прийом</>
                        }
                      </Button>

                      <Button
                        variant="outline-danger"
                        disabled={actionStatus === 'cancelling'}
                        onClick={() => setShowCancelConfirm(true)}
                        className="appointment-by-code__btn"
                      >
                        {actionStatus === 'cancelling'
                          ? <><i className="fas fa-spinner fa-spin" />Скасовуємо...</>
                          : <><i className="fas fa-times-circle" />Скасувати прийом</>
                        }
                      </Button>

                      <style>{`
                        .appointment-by-code__actions {
                          display: flex;
                          flex-wrap: wrap;
                          gap: 10px;
                          margin-top: 20px;
                          align-items: center;
                          justify-content: center;
                        }
                        .appointment-by-code__hint {
                          width: 100%;
                          text-align: center;
                          font-size: 12px;
                          color: #6c757d;
                          margin-top: -2px;
                        }
                        .appointment-by-code__btn {
                          padding: 10px 22px;
                          font-size: 15px;
                          font-weight: 600;
                          border-radius: 10px;
                          display: inline-flex;
                          align-items: center;
                          gap: 8px;
                          transition: opacity 0.2s;
                          white-space: nowrap;
                        }
                        @media (max-width: 480px) {
                          .appointment-by-code__btn {
                            width: 100%;
                            justify-content: center;
                          }
                        }
                      `}</style>
                    </div>
                  )}

                  {isCancelled && (
                    <div style={{
                      marginTop: '20px',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      backgroundColor: '#fff5f5',
                      border: '1px solid #f5c6cb',
                      color: '#721c24',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontSize: '15px',
                      fontWeight: '500',
                    }}>
                      <i className="fas fa-times-circle" style={{ fontSize: '20px', color: '#dc3545', flexShrink: 0 }} />
                      Цей прийом скасовано.
                    </div>
                  )}

                  {actionStatus === 'action_error' && (
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      color: '#856404',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                    }}>
                      <i className="fas fa-exclamation-triangle" style={{ flexShrink: 0 }} />
                      Сталася помилка. Спробуйте ще раз.
                    </div>
                  )}
                </div>
              </div>
            </li>
          </ul>
        </div>

        <Modal show={showCancelConfirm} onHide={() => setShowCancelConfirm(false)} aria-labelledby="cancel-appt-modal-title">
          <Modal.Header closeButton>
            <Modal.Title id="cancel-appt-modal-title">Скасувати запис</Modal.Title>
          </Modal.Header>
          <Modal.Body>Ви дійсно бажаєте відмінити цей запис на прийом?</Modal.Body>
          <Modal.Footer>
            <div className="container">
              <div className="row">
                <div className="col-6">
                  <Button variant="outline-primary" onClick={() => setShowCancelConfirm(false)}>
                    Ні
                  </Button>
                </div>
                <div className="col-6">
                  <Button variant="primary" onClick={handleCancel}>
                    Так, підтвердити
                  </Button>
                </div>
              </div>
            </div>
          </Modal.Footer>
        </Modal>

        {!window.ReactNativeWebView && (
          <div className="app-promo-block">
            <div className="app-market-search-row" role="presentation">
              <img src="/favicon.ico" alt="Лікарі 24/7" className="app-market-search-row__icon" />
              <div className="app-market-search-row__meta">
                <div className="app-market-search-row__name">Лікарі 24/7</div>
                <div className="app-market-search-row__company">ТОВ "СІЕТ Холдінг"</div>
              </div>
            </div>
            <p className="app-promo-title">Рекомендуємо встановити наш застосунок</p>
            <p className="app-promo-text">
              Отримуйте нагадування про прийоми, переглядайте свої записи та медичні дані прямо на телефоні.<br />
              Рекомендуємо використовувати застосунок Likari 24/7.
            </p>
            <AppsList />
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Header city={city} />

      <main className="visit-by-code-page">
        <div className="container">
          {status === 'loading' && (
            <div className="visit-by-code-page__message">
              <i className="fas fa-spinner fa-spin visit-by-code-page__spinner" />
              <p>Завантаження...</p>
            </div>
          )}

          {status === '404' && (
            <div className="visit-by-code-page__message visit-by-code-page__message--error">
              <div className="visit-by-code-page__message-icon">
                <i className="fas fa-search" />
              </div>
              <h2>Запис не знайдено</h2>
              <p>Посилання недійсне або запис не існує</p>
            </div>
          )}

          {status === '410' && (
            <div className="visit-by-code-page__message visit-by-code-page__message--expired">
              <div className="visit-by-code-page__message-icon">
                <i className="fas fa-clock" />
              </div>
              <h2>Посилання застаріло</h2>
              <p>Термін дії посилання минув.</p>
            </div>
          )}

          {status === 'error' && (
            <div className="visit-by-code-page__message visit-by-code-page__message--error">
              <div className="visit-by-code-page__message-icon">
                <i className="fas fa-exclamation-triangle" />
              </div>
              <h2>Помилка</h2>
              <p>Сталася помилка. Спробуйте оновити сторінку.</p>
            </div>
          )}

          {status === 'ok' && renderAppointmentInfo()}
        </div>
      </main>

      <Footer />
    </div>
  );
}

const mapStateToProps = ({ user }) => ({ cities: user.cities, appointment_promo: user?.appointment_promo });

export default connect(mapStateToProps, { updateCity })(AppointmentByCode);
